import React, { useState, useEffect, useCallback, useRef } from 'react';
import './RegistrationComponents.css';
import { userAPI, powerLocationAPI, userLocationConnectorAPI, showApiError } from '../../services/api';

const UserPowerPlantConnection = () => {
  const [formData, setFormData] = useState({
    userId: '',
    powerPlantId: ''
  });


  const [users, setUsers] = useState([]);
  const [powerPlants, setPowerPlants] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userSearchKeyword, setUserSearchKeyword] = useState('');
  const [powerPlantSearchKeyword, setPowerPlantSearchKeyword] = useState('');
  const [showUserList, setShowUserList] = useState(false);
  const [showPowerPlantList, setShowPowerPlantList] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const isInitialLoad = useRef(true);
  const searchInputRef = useRef(null);

  // 데이터 로드 함수들
  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll({ limit: 100 });
      console.log('Users API response:', response);
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadPowerPlants = async () => {
    try {
      setLoading(true);
      const response = await powerLocationAPI.getAll({ limit: 100 });
      console.log('Power plants API response:', response);
      setPowerPlants(response.data || []);
    } catch (err) {
      console.error('Failed to load power plants:', err);
      setError('발전소 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 연결 목록 로드
  const loadConnections = useCallback(async (reset = false, keyword = searchKeyword) => {
    if (loading) return;
    
    console.log('loadConnections called - reset:', reset, 'loading:', loading, 'keyword:', keyword);
    setLoading(true);
    try {
      const condition = {
        keyword: keyword,
        cursor: reset ? null : cursor,
        limit: 20
      };
      
      console.log('Connections API call condition:', condition);
      const response = await userLocationConnectorAPI.getAll(condition);
      
      console.log('UserPowerPlantConnection - API response:', response);
      console.log('UserPowerPlantConnection - Connections data:', response.data);
      
      if (reset) {
        console.log('Resetting connections with:', response.data);
        setConnections(response.data || []);
      } else {
        console.log('Adding to existing connections:', response.data);
        setConnections(prev => {
          const newData = [...prev, ...(response.data || [])];
          console.log('New connections state:', newData);
          return newData;
        });
      }
      
      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
    } catch (error) {
      console.error('연결 목록 로드 실패:', error);
      setError('연결 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      const loadInitialData = async () => {
        setInitialLoading(true);
        try {
          await Promise.all([
            loadUsers(),
            loadPowerPlants(),
            loadConnections(true, '')
          ]);
        } finally {
          setInitialLoading(false);
        }
      };
      loadInitialData();
      isInitialLoad.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const handleInputChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData(prev => ({
  //     ...prev,
  //     [name]: value
  //   }));
  // };

  // 검색된 사용자 목록
  const getFilteredUsers = () => {
    if (!userSearchKeyword.trim()) return users;
    return users.filter(user => 
      (user.name && user.name.toLowerCase().includes(userSearchKeyword.toLowerCase())) ||
      (user.username && user.username.toLowerCase().includes(userSearchKeyword.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(userSearchKeyword.toLowerCase()))
    );
  };

  // 검색된 발전소 목록
  const getFilteredPowerPlants = () => {
    if (!powerPlantSearchKeyword.trim()) return powerPlants;
    return powerPlants.filter(plant => 
      (plant.locationName && plant.locationName.toLowerCase().includes(powerPlantSearchKeyword.toLowerCase())) ||
      (plant.serialCode && plant.serialCode.toLowerCase().includes(powerPlantSearchKeyword.toLowerCase())) ||
      (plant.location && plant.location.toLowerCase().includes(powerPlantSearchKeyword.toLowerCase()))
    );
  };

  // 사용자 선택
  const handleUserSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      userId: user.id
    }));
    setShowUserList(false);
    setUserSearchKeyword('');
  };

  // 발전소 선택
  const handlePowerPlantSelect = (plant) => {
    setFormData(prev => ({
      ...prev,
      powerPlantId: plant.id
    }));
    setShowPowerPlantList(false);
    setPowerPlantSearchKeyword('');
  };

  // 선택된 사용자 정보
  const getSelectedUser = () => {
    return users.find(user => user.id === parseInt(formData.userId));
  };

  // 선택된 발전소 정보
  const getSelectedPowerPlant = () => {
    return powerPlants.find(plant => plant.id === parseInt(formData.powerPlantId));
  };

  // 검색
  const handleSearch = () => {
    setCursor(null);
    setHasMore(true);
    loadConnections(true, searchKeyword);
    
    // 검색 후 포커스 유지
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  // 엔터키 검색 핸들러
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadConnections();
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
    // 이미 연결된 조합인지 확인
    const existingConnection = connections.find(
        conn => conn.user?.id === parseInt(formData.userId) && 
                 conn.powerLocation?.id === parseInt(formData.powerPlantId)
    );

    if (existingConnection) {
      alert('이미 연결된 사용자-발전소 조합입니다.');
      return;
    }

      const connectionData = {
      userId: parseInt(formData.userId),
        locationId: parseInt(formData.powerPlantId)
      };
      
      await userLocationConnectorAPI.create(connectionData);
      
    setFormData({
      userId: '',
      powerPlantId: ''
    });
      
    alert('사용자-발전소 연결이 성공적으로 등록되었습니다!');
      
      // 목록 새로고침
      setCursor(null);
      setHasMore(true);
      loadConnections(true);
    } catch (error) {
      console.error('연결 등록 실패:', error);
      showApiError(error);
    }
  };

  // 행 클릭 핸들러
  const handleRowClick = (connection) => {
    setSelectedConnection(connection);
    setShowDetailModal(true);
  };

  // 삭제 실행
  const handleDeleteConfirm = async () => {
    try {
      await userLocationConnectorAPI.delete(selectedConnection.id);
      alert('연결이 성공적으로 삭제되었습니다!');
      setShowDeleteModal(false);
      setShowDetailModal(false);
      setSelectedConnection(null);
      
      // 목록 새로고침
      setCursor(null);
      setHasMore(true);
      loadConnections(true);
    } catch (error) {
      console.error('연결 삭제 실패:', error);
      showApiError(error);
    }
  };

  // 전체 페이지 로딩 제거 - 연결 목록만 로딩 표시

  if (error) {
    return (
      <div className="registration-component">
        <div className="error-container">
          <p>❌ {error}</p>
          <button className="btn btn-primary" onClick={() => {
            setError(null);
            loadUsers();
            loadPowerPlants();
          }}>
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-component user-power-plant-connection">
      <div className="component-header">
        <h2>🔗 사용자-발전소 연결</h2>
        <p>사용자와 발전소 간의 연결을 관리합니다.</p>
      </div>

      <div className="registration-layout">
        <div className="form-section">
          <h3>새 연결 등록</h3>
          <form onSubmit={handleSubmit} className="registration-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="userId">사용자 선택 *</label>
                <div className="search-select-container">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="사용자 이름, 아이디, 이메일로 검색..."
                      value={userSearchKeyword}
                      onChange={(e) => setUserSearchKeyword(e.target.value)}
                      onFocus={() => setShowUserList(true)}
                      className="search-input"
                    />
                    <button 
                      type="button" 
                      className="search-btn"
                      onClick={() => setShowUserList(!showUserList)}
                    >
                      🔍
                    </button>
                  </div>
                  
                  {formData.userId && (
                    <div className="selected-item">
                      <span className="selected-text">
                        ID: {getSelectedUser()?.username || 'username'} - 이름: {getSelectedUser()?.name || '이름 없음'} - 권한: {getSelectedUser()?.role || 'USER'}
                      </span>
                      <button 
                        type="button" 
                        className="clear-btn"
                        onClick={() => setFormData(prev => ({ ...prev, userId: '' }))}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  {showUserList && (
                    <div className="search-dropdown">
                      <div className="dropdown-header">
                        <span>사용자 목록</span>
                        <button 
                          type="button" 
                          className="close-btn"
                          onClick={() => setShowUserList(false)}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="dropdown-list">
                        {getFilteredUsers().length > 0 ? (
                          getFilteredUsers().map(user => (
                            <div 
                              key={user.id} 
                              className="dropdown-item"
                              onClick={() => handleUserSelect(user)}
                            >
                              <div className="item-name">
                                ID: {user.username || 'username'} - 이름: {user.name || '이름 없음'} - 권한: {user.role || 'USER'}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-results">검색 결과가 없습니다.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="powerPlantId">발전소 선택 *</label>
                <div className="search-select-container">
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      placeholder="발전소명, 시리얼코드, 주소로 검색..."
                      value={powerPlantSearchKeyword}
                      onChange={(e) => setPowerPlantSearchKeyword(e.target.value)}
                      onFocus={() => setShowPowerPlantList(true)}
                      className="search-input"
                    />
                    <button 
                      type="button" 
                      className="search-btn"
                      onClick={() => setShowPowerPlantList(!showPowerPlantList)}
                    >
                      🔍
                    </button>
                  </div>
                  
                  {formData.powerPlantId && (
                    <div className="selected-item">
                      <span className="selected-text">
                        발전소명: {getSelectedPowerPlant()?.locationName || '발전소명 없음'} - 코드: {getSelectedPowerPlant()?.serialCode || '시리얼코드 없음'}
                      </span>
                      <button 
                        type="button" 
                        className="clear-btn"
                        onClick={() => setFormData(prev => ({ ...prev, powerPlantId: '' }))}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  {showPowerPlantList && (
                    <div className="search-dropdown">
                      <div className="dropdown-header">
                        <span>발전소 목록</span>
                        <button 
                          type="button" 
                          className="close-btn"
                          onClick={() => setShowPowerPlantList(false)}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="dropdown-list">
                        {getFilteredPowerPlants().length > 0 ? (
                          getFilteredPowerPlants().map(plant => (
                            <div 
                              key={plant.id} 
                              className="dropdown-item"
                              onClick={() => handlePowerPlantSelect(plant)}
                            >
                              <div className="item-name">
                                발전소명: {plant.locationName || '발전소명 없음'} - 코드: {plant.serialCode || '시리얼코드 없음'}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-results">검색 결과가 없습니다.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                연결 등록
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setFormData({
                userId: '', powerPlantId: ''
              })}>
                초기화
              </button>
            </div>
          </form>
        </div>

        <div className="list-section">
          <div className="list-header">
          <h3>연결 목록</h3>
            <div className="search-section">
              <input
                key="connection-search"
                ref={searchInputRef}
                type="text"
                placeholder="사용자 이름, 아이디, 발전소명으로 검색"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
                autoFocus
              />
              <button onClick={handleSearch} className="btn btn-primary btn-sm">
                검색
              </button>
            </div>
          </div>
          <div className="data-table">
            <div className="table-header">
              <div className="table-cell">사용자 ID</div>
              <div className="table-cell">사용자 이름</div>
              <div className="table-cell">발전소명</div>
              <div className="table-cell">시리얼코드</div>
              <div className="table-cell">연결일시</div>
            </div>
            {initialLoading ? (
              <div className="loading-row">
                <div className="loading-spinner"></div>
                <span>데이터를 불러오는 중...</span>
              </div>
            ) : connections.length > 0 ? (
              connections.map(connection => (
                <div 
                  key={connection.id} 
                  className="table-row connection-row"
                  onClick={() => handleRowClick(connection)}
                >
                  <div className="table-cell">
                    {connection.user?.username || 'username'}
                  </div>
                  <div className="table-cell">
                    {connection.user?.name || '이름 없음'}
                  </div>
                  <div className="table-cell">
                    {connection.powerLocation?.locationName || '발전소명 없음'}
                  </div>
                  <div className="table-cell">
                    {connection.powerLocation?.serialCode || '시리얼코드 없음'}
                </div>
                <div className="table-cell">
                    {connection.createdAt ? new Date(connection.createdAt).toLocaleString('ko-KR') : '-'}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">
                <p>등록된 연결이 없습니다.</p>
              </div>
            )}
            
            {/* 무한스크롤 로딩 */}
            {loading && (
              <div className="loading-row">
                <div className="loading-spinner"></div>
                <span>로딩 중...</span>
              </div>
            )}
            
            {/* 더 보기 버튼 */}
            {hasMore && !loading && (
              <div className="load-more-section">
                <button onClick={loadMore} className="btn btn-secondary">
                  더 보기
                </button>
              </div>
            )}
            
            {/* 더 이상 데이터가 없을 때 */}
            {!hasMore && connections.length > 0 && (
              <div className="no-more-data">
                <span>모든 데이터를 불러왔습니다.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 상세정보 모달 */}
      {showDetailModal && selectedConnection && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>연결 상세정보</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowDetailModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="connection-detail">
                <div className="detail-section">
                  <h4>사용자 정보</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">ID:</span>
                      <span className="detail-value">{selectedConnection.id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">사용자명:</span>
                      <span className="detail-value">{selectedConnection.user?.name || '이름 없음'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">사용자 ID:</span>
                      <span className="detail-value">{selectedConnection.user?.username || 'username'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">권한:</span>
                      <span className="detail-value">{selectedConnection.user?.role || 'USER'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">이메일:</span>
                      <span className="detail-value">{selectedConnection.user?.email || '정보 없음'}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>발전소 정보</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">발전소명:</span>
                      <span className="detail-value">{selectedConnection.powerLocation?.locationName || '발전소명 없음'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">시리얼코드:</span>
                      <span className="detail-value">{selectedConnection.powerLocation?.serialCode || '시리얼코드 없음'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">주소:</span>
                      <span className="detail-value">{selectedConnection.powerLocation?.location || '정보 없음'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">용량:</span>
                      <span className="detail-value">{selectedConnection.powerLocation?.capacity || 0}kW</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">위치:</span>
                      <span className="detail-value">
                        {selectedConnection.powerLocation?.latitude && selectedConnection.powerLocation?.longitude 
                          ? `${selectedConnection.powerLocation.latitude}, ${selectedConnection.powerLocation.longitude}`
                          : '정보 없음'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>연결 정보</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">연결일시:</span>
                      <span className="detail-value">
                        {selectedConnection.createdAt ? new Date(selectedConnection.createdAt).toLocaleString('ko-KR') : '-'}
                  </span>
                </div>
                    <div className="detail-item">
                      <span className="detail-label">상태:</span>
                      <span className="detail-value">연결됨</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
                    <button 
                className="btn btn-secondary" 
                onClick={() => setShowDetailModal(false)}
                    >
                닫기
                    </button>
                    <button 
                className="btn btn-danger" 
                onClick={() => {
                  setShowDetailModal(false);
                  setShowDeleteModal(true);
                }}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && selectedConnection && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>연결 삭제 확인</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowDeleteModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <div className="warning-icon">⚠️</div>
                <p><strong>정말로 이 연결을 삭제하시겠습니까?</strong></p>
                <div className="connection-info">
                  <p><strong>사용자:</strong> {selectedConnection.user?.name || '이름 없음'} ({selectedConnection.user?.username || 'username'})</p>
                  <p><strong>발전소:</strong> {selectedConnection.powerLocation?.locationName || '발전소명 없음'} ({selectedConnection.powerLocation?.serialCode || '시리얼코드 없음'})</p>
                </div>
                <p className="warning-text">이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleDeleteConfirm}
              >
                삭제
              </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default UserPowerPlantConnection;
